import { LogoBuilder } from '@logocode/logo-design-engine/lib/library/logo-builder/LogoBuilder'
import { SVG } from '@svgdotjs/svg.js'
import { strToBlob } from './binary'
import { EventBus, Events } from '@/lib/events'
import { createNewLogoId } from '../lde/util'
import { isNonEmptyArray } from './array'
import { ntc } from '@/lib/helpers/name-to-color'
import { bindFetch } from '@/lib/lde/fetch'

export const getPaletteEventSlug = color => {
  if (!color) return null
  if (typeof color === 'string') return ntc.name(color)[3]
  if (color.event_slug) return color.event_slug
  return color
}

/**
 * Accept different color representations and return a single hex string
 * @param {*} color Color hex string, color object (e.g. gradient),
 * or array of either
 * @returns Color as a hex string
 */
export const getPaletteHexValue = color => {
  if (!color) return null
  if (color.solid) return color.solid
  if (Array.isArray(color)) return getPaletteHexValue(color[0])
  return color
}

export const getPaletteHexValues = palette => {
  const primary = getPaletteHexValue(palette.primary)
  const secondary = getPaletteHexValue(palette.secondary)
  const accent = getPaletteHexValue(palette.accent)
  const background = getPaletteHexValue(palette.background)
  const tertiary = getPaletteHexValue(palette.tertiary)
  const quaternary = getPaletteHexValue(palette.quaternary)
  return {
    primary,
    secondary,
    accent,
    background,
    tertiary,
    quaternary,
  }
}

export const processLDELogoForSaving = (logo, wordmark, slogan) => {
  const assets = {
    ...logo.assets,
    wordmark_fonts: [logo.assets.fonts.wordmark],
    slogan_fonts: logo.assets.fonts.slogan ? [logo.assets.fonts.slogan] : [],
  }
  return {
    id: logo.id || createNewLogoId(),
    svg: logo.svg_code,
    ...logo,
    assets,
    wordmark,
    slogan,
  }
}

export const processAndFavoriteClientSideLogo = async (
  logo,
  eid,
  favoriteFunction
) => {
  const logoId = logo.id ?? createNewLogoId()

  const logo_to_share = {
    id: logoId,
    wordmark: logo.assets.template.text.wordmark ?? undefined,
    slogan: logo.assets.template.text.slogan ?? undefined,
    ...logo,
    svg: logo.svg_code,
  }
  const favorited = await favoriteFunction(logo_to_share, eid)
  return {
    ...favorited,
    svg: logo.svg ?? logo.svg_code,
  }
}

export const processClientSideLogoForUpload = (
  id,
  logo,
  user,
  eid,
  disableUserId = false
) => {
  const { assets } = logo
  const wordmark_font = logo.assets.fonts
    ? logo.assets.fonts.wordmark
    : logo.assets.wordmark_fonts[0]
  const reduced_wordmark_font = {
    id: wordmark_font.id,
    name: wordmark_font.name,
  }

  const cloned_logo = {
    ...logo,
    id,
    eid,
    user_id: user ? user.id : undefined,
    saved_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    saved: true,
    template: logo.template,
    ttl: null,
    assets: {
      ...assets,
      fonts: {
        primary: reduced_wordmark_font,
        wordmark: reduced_wordmark_font,
      },
      wordmark_fonts: [reduced_wordmark_font],
      wordmark_font_families: undefined,
    },
    svg: undefined,
    svg_code: undefined,
    asset_data: undefined,
    previews: undefined,
    preview: undefined,
    url: undefined,
  }

  let icon
  if (logo.assets.icons.icon) {
    icon = logo.assets.icons.icon
  } else if (isNonEmptyArray(logo.assets.icons)) {
    icon = logo.assets.icons[0]
  }
  if (icon) {
    const reduced_icon = {
      id: icon.id,
      preview_url: icon.preview_url,
    }
    cloned_logo.assets.icons = [reduced_icon]
  } else {
    cloned_logo.assets.icons = []
  }

  let slogan_font
  if (logo.assets.slogan_fonts && logo.assets.slogan_fonts.length > 0) {
    slogan_font = logo.assets.slogan_fonts[0]
  } else if (logo.assets.fonts && logo.assets.fonts.slogan) {
    slogan_font = logo.assets.fonts.slogan
  }

  if (slogan_font) {
    const reduced_slogan_font = {
      id: slogan_font.id,
      name: slogan_font.name,
    }
    cloned_logo.assets.fonts.secondary = reduced_slogan_font
    cloned_logo.assets.fonts.slogan = reduced_slogan_font
    cloned_logo.assets.slogan_fonts = [reduced_slogan_font]
    cloned_logo.assets.slogan_font_families = undefined
  } else {
    cloned_logo.assets.slogan_fonts = []
  }
  if (disableUserId) {
    cloned_logo.user_id = undefined
  }
  const formdata = new FormData()
  formdata.append(
    'metadata',
    strToBlob(JSON.stringify(cloned_logo)),
    `logo_${id}.json`
  )
  formdata.append('svg', strToBlob(logo.svg), `logo_${id}.svg`)
  return formdata
}

export function addLogoEventHandlers(svgroot) {
  const text0 = svgroot.findOne('#text-0')
  const text1 = svgroot.findOne('#text-1')
  const text2 = svgroot.findOne('#text-2')

  // In multi-colour templates, the slogan becomes text-2
  const sloganTarget = text2 || text1

  // In multi-colour templates, the wordmark is made up of text-0 and text-1
  const wordmarkTarget = text2 ? [text0, text1] : text0

  // Map control handles to logo element selectors
  const items = [
    { control: 'business-name', target: wordmarkTarget },
    { control: 'slogan', target: sloganTarget },
    { control: 'icons', target: svgroot.findOne('#icon-0') },
  ]

  const boxes = []
  items.forEach(({ control, target }) => {
    // If there's nothing returned for the selector, the logo doesn't have that element
    if (!target) return

    // prettier-ignore
    let w;
    let h
    let x
    let cx
    let cy = 0

    // If we're in a multi-colour template, build the box using both elements
    if (Array.isArray(target)) {
      const { width } = target[1].rbox(svgroot)

      ;({ w, h, cy, x } = target[0].rbox(svgroot))

      // Combine the widths of both elements
      w += width

      // The center is the left point plus half the total width
      cx = x + w / 2
    } else {
      // If our element is within a <mask>, we need to locate the element using it
      const mask = target.masker()

      let element = target

      if (mask) {
        // Use the masked shape as the hover target

        element = svgroot.findOne(`[mask="url(#${mask.id()})"]`)

        // If we can't find the mask, then there's nothing to do
        if (!element) return
      }

      // Grab the dimensions of the target relative to the canvas
      ;({ w, h, cx, cy } = element.rbox(svgroot))
    }

    // Offset the new box slightly so it pads the element
    const offset = 12
    const centerOffset = offset / offset

    // Draw a box over the element so we have a solid click target
    const box = svgroot
      .rect(w + offset, h + offset)
      .center(cx + centerOffset, cy + centerOffset)
      .radius(2.5)

    // Add a click handler that fires an event to the Editor
    box.click(() => EventBus.$emit(Events.EditorControlClicked, control))

    // Add a class for us to style later
    box.addClass('logo-element-hover-target')
    boxes.push(box)
  })

  return boxes
}

/**
 * parse SVG string to XMLElement object
 * @param {*} svg svg to convert
 * @returns XMLElement with svg as root
 */
export function createXmlDocFromSvgString(svg) {
  const parser = new DOMParser()
  return parser.parseFromString(svg, 'image/svg+xml')
}

/**
 * parse SVG document and return first child of element with id="tight-boudns"
 * @param {*} svg svg as string
 * @returns tight bounds object as XMLElement
 */
export function extractTightBoundsFromLogoXml(svg) {
  const logoDOM = createXmlDocFromSvgString(svg)
  return logoDOM.getElementById('tight-bounds').children[0]
}

/**
 * Recolor logo using XMLElement apis
 * @param {*} xmldoc svg as XMLElemetn
 * @param {*} file File containing re-coloring data
 */
export function reColorLogoInXmlDoc(xmldoc, file) {
  const colors = ['primary', 'secondary', 'accent', 'quaternary', 'tertiary']
  colors.forEach(color => {
    if (file[color]) reColorElements(xmldoc, color, file[color])
  })
}

/**
 * Recolor logo to white palette and return result as base 64 string
 * @param {*} xmldoc logo svg as XMLElement
 * @returns white colored logo as base64 string
 */
export function generateWhitePreview(xmldoc) {
  reColorLogoInXmlDoc(xmldoc, {
    accent: '#ffffff',
    primary: '#ffffff',
    secondary: '#ffffff',
    quaternary: '#000000',
    tertiary: '#ffffff',
  })
  return convertSvgToBase64(xmldoc.outerHTML)
}

/**
 * Recolor logo to black palette and return result as base 64 string
 * @param {*} xmldoc logo svg as XMLElement
 * @returns black colored logo as base64 string
 */
export function generateBlackPreview(xmldoc) {
  reColorLogoInXmlDoc(xmldoc, {
    accent: '#000000',
    primary: '#000000',
    secondary: '#000000',
    quaternary: '#ffffff',
    tertiary: '#000000',
  })
  return convertSvgToBase64(xmldoc.outerHTML)
}

export function generateInvertedPreview(xmldoc, palette) {
  reColorLogoInXmlDoc(xmldoc, {
    accent: palette.background,
    primary: palette.background,
    secondary: palette.background,
    quaternary: palette.primary,
    tertiary: palette.background,
  })
  return convertSvgToBase64(xmldoc.outerHTML)
}

/**
 * Recolor all elements in svg that have data-fill-palette-color="colorName" to have fill="colorValue"
 * @param {*} xmldoc svg as XMLElement
 * @param {*} colorName colorname IE primary, secondary
 * @param {*} colorValue color value as hex
 */
export function reColorElements(xmldoc, colorName, colorValue) {
  const elements = xmldoc.querySelectorAll(
    `[data-fill-palette-color=${colorName}]`
  )
  elements.forEach(currentValue => {
    currentValue.setAttribute('fill', colorValue)
  })
  const strokeElements = xmldoc.querySelectorAll(
    `[data-stroke-palette-color=${colorName}]`
  )
  strokeElements.forEach(currentValue => {
    currentValue.setAttribute('stroke', colorValue)
  })
}

/**
 * Convert SVG string into base64 data URI to be used in img tags
 * @param {*} svg
 * @returns
 */
export function convertSvgToBase64(svg) {
  return `data:image/svg+xml;base64,${Buffer.from(svg).toString('base64')}`
}

/**
 * Generate all logo variants and return them in base64 strings
 * @param {*} logo logo object to create variants for
 * @param {*} api_base api base url to fetch assets from
 * @returns object containing all logo variants
 */
export async function generateLogoVariants(logo, api_base) {
  const t0 = performance.now()
  const logoBuilder = new LogoBuilder(api_base, {
    prefetchAssets: false,
    fetchApi: bindFetch(),
  })
  const svg = SVG(logo.svgRoot)
  const canvas = logo.canvasdom
  const assets = await logoBuilder.prefetchAssetsForLogo(logo)

  const no_icon = await logoBuilder.getLogo(
    logo,
    {
      ...assets,
      icons: [],
    },
    svg,
    canvas,
    'default',
    true
  )

  const no_slogan = await logoBuilder.getLogo(
    { ...logo, slogan: '' },
    assets,
    svg,
    canvas,
    'default',
    true
  )

  const no_slogan_no_icon = await logoBuilder.getLogo(
    { ...logo, slogan: '' },
    {
      ...assets,
      icons: [],
    },
    svg,
    canvas,
    'default',
    true
  )

  const icononly = await logoBuilder.getLogo(
    logo,
    assets,
    svg,
    canvas,
    'icononly',
    'default',
    true
  )

  const left = await logoBuilder.getLogo(
    changeLogoIconOrientation(logo, 'left'),
    assets,
    svg,
    canvas,
    'default',
    true
  )

  const right = await logoBuilder.getLogo(
    changeLogoIconOrientation(logo, 'right'),
    assets,
    svg,
    canvas,
    'default',
    true
  )

  const top = await logoBuilder.getLogo(
    changeLogoIconOrientation(logo, 'top'),
    assets,
    svg,
    canvas,
    'default',
    true
  )

  const bottom = await logoBuilder.getLogo(
    changeLogoIconOrientation(logo, 'bottom'),
    assets,
    svg,
    canvas,
    'default',
    true
  )

  const icononlyxml = createXmlDocFromSvgString(icononly.svg_code).children[0]
  const icononlypreviews = createPreviewsForXmlDoc(icononlyxml)

  const no_slogan_no_icon_xml = createXmlDocFromSvgString(
    no_slogan_no_icon.svg_code
  ).children[0]
  const no_slogan_no_icon_previews = createPreviewsForXmlDoc(
    no_slogan_no_icon_xml
  )
  const t1 = performance.now()
  console.debug(`Call to generate variant logos took ${t1 - t0} milliseconds.`)
  return {
    icononly: icononlypreviews,
    without_slogan_without_icon: no_slogan_no_icon_previews,
    top: convertSvgToBase64(top.svg_code),
    left: convertSvgToBase64(left.svg_code),
    right: convertSvgToBase64(right.svg_code),
    bottom: convertSvgToBase64(bottom.svg_code),
    without_icon: convertSvgToBase64(no_icon.svg_code),
    without_slogan: convertSvgToBase64(no_slogan.svg_code),
  }
}

/**
 * takes an XMLElement object and generates base64 data urls for the document as passed, then generates black and white previews seperately
 * @param {*} xmldoc XMLElement logo svg object
 * @returns object containing default, white and black previews as base64 data urls
 */
export function createPreviewsForXmlDoc(xmldoc) {
  const tight_bounds = convertSvgToBase64(xmldoc.outerHTML)

  const tight_bounds_black = generateBlackPreview(xmldoc)

  const tight_bounds_white = generateWhitePreview(xmldoc)
  return {
    default: tight_bounds,
    black: tight_bounds_black,
    white: tight_bounds_white,
  }
}

/**
 * Take a logo object and return a shallow copy with the orientation changed
 * @param {*} logo logo object to modify
 * @param {*} orientation icon orientation to use
 * @returns logo metadata
 */
export function changeLogoIconOrientation(logo, orientation) {
  return {
    ...logo,
    assets: {
      ...logo.assets,
      template: {
        ...logo.assets.template,
        orientation,
      },
    },
  }
}

/**
 * Given a dimension (width/height) and a margin value, find out what percentage of that dimension will be margin
 */
export function calculateMarginPercentage(dimension, margin) {
  return ((dimension - margin) / dimension) * 100
}
